import React, { useRef, useCallback } from 'react';
import { Form, Alert } from 'react-bootstrap';
import { FaExclamationTriangle } from 'react-icons/fa';

/**
 * Componente para el formulario de Puntos de Calibración
 * Muestra una tabla con inputs para cada punto de calibración de una magnitud
 * y opcionalmente un punto de histéresis
 */
const PuntosCalibracionForm = ({
  magnitud,
  datosPuntosCalibracion,
  datoHisteresis,
  onChangePunto,
  onChangeHisteresis
}) => {
  const { cantidadPuntos:cantPuntos, histeresis:requiereHisteresis, histeresisEnTodosLosPuntos } = magnitud;
  const showHisteresis = requiereHisteresis && !histeresisEnTodosLosPuntos;

  // Validación: el valor del punto de histéresis DEBE coincidir con uno de los
  // puntos de calibración (Punto 1..N). La histéresis se registra típicamente
  // en el penúltimo punto de calibración — valores distintos indican error de
  // digitación o concepto. Si el usuario escribe algo que no coincide, se
  // muestra un banner naranja de alerta debajo de la tabla.
  const histeresisMatchPunto = React.useMemo(() => {
    if (!showHisteresis) return { valido: true, mostrar: false };
    const raw = datoHisteresis;
    if (raw === '' || raw === null || raw === undefined) return { valido: true, mostrar: false };
    const val = Number(raw);
    if (Number.isNaN(val)) return { valido: false, mostrar: true };
    const puntos = (datosPuntosCalibracion || [])
      .map((v) => Number(v))
      .filter((v) => !Number.isNaN(v));
    if (puntos.length === 0) return { valido: true, mostrar: false };
    // Tolerancia pequeña (1e-9) por posibles errores de representación float.
    const match = puntos.some((p) => Math.abs(p - val) < 1e-9);
    return { valido: match, mostrar: !match };
  }, [datoHisteresis, datosPuntosCalibracion, showHisteresis]);

  // Refs para navegación entre inputs
  const inputRefs = useRef({});

  // Función para obtener/crear ref
  const getInputRef = (index) => {
    if (!inputRefs.current[index]) {
      inputRefs.current[index] = React.createRef();
    }
    return inputRefs.current[index];
  };

  // Función para navegar entre inputs
  const handleNavigate = useCallback((currentIdx, direction) => {
    const totalPuntos = cantPuntos + (showHisteresis ? 1 : 0);
    let newIdx = currentIdx;

    switch (direction) {
      case 'left':
        if (currentIdx > 0) {
          newIdx = currentIdx - 1;
        }
        break;
      case 'right':
        if (currentIdx < totalPuntos - 1) {
          newIdx = currentIdx + 1;
        }
        break;
      default:
        return;
    }

    const newRef = inputRefs.current[newIdx];
    if (newRef && newRef.current) {
      newRef.current.focus();
      newRef.current.select();
    }
  }, [cantPuntos, showHisteresis]);

  // Handler para teclas en el input
  const handleKeyDown = (e, index) => {
    // Prevenir que flechas arriba/abajo cambien el valor
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
    }

    // Navegación entre celdas
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      handleNavigate(index, 'left');
    } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
      e.preventDefault();
      handleNavigate(index, 'right');
    }
  };

  return (
    <div className="mediciones-table-container">
      <table className="puntos-calibracion-table">
        <thead>
          <tr>
            <th className="puntos-calibracion-table__th puntos-calibracion-table__th--index">
              #
            </th>
            {Array.from({ length: cantPuntos }).map((_, idx) => (
              <th key={idx} className="puntos-calibracion-table__th">
                Punto {idx + 1}
              </th>
            ))}
            {showHisteresis && (
              <th className="puntos-calibracion-table__th puntos-calibracion-table__th--histeresis">
                Histéresis
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="puntos-calibracion-table__td puntos-calibracion-table__td--index">
              Valor
            </td>
            {Array.from({ length: cantPuntos }).map((_, idxPunto) => (
              <td key={idxPunto} className="puntos-calibracion-table__td">
                <Form.Control
                  type="number"
                  step="0.001"
                  onWheel={(e) => e.currentTarget.blur()}
                  className="puntos-calibracion-table__input"
                  placeholder={`Punto ${idxPunto + 1}`}
                  value={datosPuntosCalibracion?.[idxPunto] ?? ''}
                  onChange={(e) => onChangePunto(idxPunto, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, idxPunto)}
                  ref={getInputRef(idxPunto)}
                />
              </td>
            ))}
            {showHisteresis && (
              <td className="puntos-calibracion-table__td puntos-calibracion-table__td--histeresis">
                <Form.Control
                  type="number"
                  step="0.001"
                  onWheel={(e) => e.currentTarget.blur()}
                  className="puntos-calibracion-table__input"
                  placeholder="Valor histéresis"
                  value={datoHisteresis ?? ''}
                  onChange={(e) => onChangeHisteresis(e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, cantPuntos)}
                  ref={getInputRef(cantPuntos)}
                  isInvalid={histeresisMatchPunto.mostrar}
                  style={histeresisMatchPunto.mostrar ? { borderColor: '#fd7e14', borderWidth: 2 } : undefined}
                />
              </td>
            )}
          </tr>
        </tbody>
      </table>

      {histeresisMatchPunto.mostrar && (
        <Alert variant="warning" className="mt-2 mb-0 py-2 small d-flex align-items-start"
          style={{ borderColor: '#fd7e14', backgroundColor: '#fff3cd' }}>
          <FaExclamationTriangle className="me-2 mt-1" style={{ color: '#fd7e14' }} />
          <div>
            <strong>El valor de histéresis no coincide con ninguno de los puntos de calibración.</strong>
            <div className="text-muted" style={{ fontSize: '0.8rem' }}>
              La histéresis se toma normalmente desde el penúltimo punto. Puntos actuales:{' '}
              {(datosPuntosCalibracion || []).filter((v) => v !== '' && v !== null && v !== undefined).join(', ') || '—'}.
              Revisa que el valor coincida con uno de ellos.
            </div>
          </div>
        </Alert>
      )}
    </div>
  );
};

export default PuntosCalibracionForm;
